import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@material-ui/core';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ClientCreateForm } from 'types';
import { sanitizeAlphabetic, sanitizeNumber } from 'utils/formatter';
import { clientCreateStep2Schema } from './Step2.yup';
import { useStyles } from './styles';
import { ClientCreateStepProps } from './types';

export const Step2: React.FC<ClientCreateStepProps> = ({ onSubmit, formName, previousStepData }) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'clients' });

  const schema = useMemo(() => clientCreateStep2Schema(t), [t]);

  const { control, handleSubmit, setValue } = useForm<ClientCreateForm>({
    mode: 'onBlur',
    defaultValues: previousStepData,
    resolver: yupResolver(schema),
  });

  return (
    <form id={formName} className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.fieldLine}>
        <Controller
          name="contact.lastName"
          control={control}
          render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
            <TextField
              fullWidth
              id="lastName"
              variant="outlined"
              label={t('create.step2.lastName')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputRef={ref}
              onChange={e => {
                setValue('contact.lastName', sanitizeAlphabetic(e.target.value));
              }}
              {...inputProps}
            />
          )}
        />
      </div>
      <div className={classes.fieldLine}>
        <Controller
          name="contact.firstName"
          control={control}
          render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
            <TextField
              fullWidth
              id="firstName"
              variant="outlined"
              label={t('create.step2.firstName')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              onChange={e => {
                setValue('contact.firstName', sanitizeAlphabetic(e.target.value));
              }}
              inputRef={ref}
              {...inputProps}
            />
          )}
        />
      </div>
      <div className={classes.fieldLine}>
        <Controller
          name="contact.email"
          control={control}
          render={({ field: { ref, ...inputProps }, fieldState }) => (
            <TextField
              fullWidth
              id="email"
              variant="outlined"
              label={t('create.step2.email')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputRef={ref}
              {...inputProps}
            />
          )}
        />
      </div>
      <div className={classes.fieldLine}>
        <Controller
          name="contact.phoneNumber"
          control={control}
          render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
            <TextField
              fullWidth
              id="phoneNumber"
              variant="outlined"
              label={t('create.step2.phoneNumber')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputRef={ref}
              onChange={e => {
                setValue('contact.phoneNumber', sanitizeNumber(e.target.value));
              }}
              inputProps={{ maxLength: 12 }}
              {...inputProps}
            />
          )}
        />
      </div>
    </form>
  );
};
